import { useState, useEffect, useRef, useCallback, memo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { t, font, Button, Badge } from "@realtyflows/ui";
import { getContractTemplate, sendContractTemplate, getDeals, downloadTemplatePdf } from "../api";
import SearchSelect from "../components/SearchSelect";
import useResponsive from "../hooks/useResponsive";

// Stable inline field — manages own local state, syncs to parent on blur/change
function InlineField({ fieldKey, label, initialValue, onCommit, onSave }) {
  const [val, setVal] = useState(initialValue || "");
  const w = Math.min(Math.max((val.length || 8) * 7.5 + 30, 120), 320);
  return (
    <input
      value={val}
      onChange={e => { setVal(e.target.value); onCommit(fieldKey, e.target.value); }}
      placeholder=""
      title={label}
      className="form-field-input"
      style={{
        display: "inline-block", width: w, padding: "1px 6px", margin: "0 2px",
        border: "none", borderBottom: "2px solid #a8c8e8", background: "#dce8f5",
        fontSize: 11, fontFamily: "'Georgia', serif", color: "#0a2540", fontWeight: 600,
        outline: "none", borderRadius: 0, verticalAlign: "baseline",
      }}
      onFocus={e => { e.target.style.borderBottomColor = "#2563eb"; e.target.style.background = "#c7ddf5"; }}
      onBlur={e => { e.target.style.borderBottomColor = "#a8c8e8"; e.target.style.background = "#dce8f5"; }}
    />
  );
}

/**
 * FormEditor — MLS-style fillable form view for contract templates.
 * Renders like connectMLS: line numbers, inline blue fields, signature blocks.
 */
export default function FormEditor({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState({});
  const [sendModal, setSendModal] = useState(false);
  const [deals, setDeals] = useState([]);
  const [sendForm, setSendForm] = useState({ deal_id: "", signerName: "", signerEmail: "", signer2Name: "", signer2Email: "" });
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [saved, setSaved] = useState(false);
  const lineCounter = useRef(1);

  useEffect(() => {
    getContractTemplate(id)
      .then(tmpl => {
        setTemplate(tmpl);
        const initial = {};
        (tmpl.placeholders || []).forEach(p => { initial[p.key] = p.default || ""; });
        // Load saved draft
        try {
          const draft = localStorage.getItem(`form_draft_${id}`);
          if (draft) Object.assign(initial, JSON.parse(draft));
        } catch {}
        setFields(initial);
      })
      .catch(() => navigate("/contracts"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const showToast = (msg, variant = "success") => setToast({ msg, variant });

  const fieldsRef = useRef(fields);
  const saveTimer = useRef(null);

  // Keep ref in sync but don't trigger re-render
  useEffect(() => { fieldsRef.current = fields; }, [fields]);

  const updateField = useCallback((key, value) => {
    fieldsRef.current = { ...fieldsRef.current, [key]: value };
    setSaved(false);
  }, []);

  const saveNow = useCallback(() => {
    clearTimeout(saveTimer.current);
    const data = fieldsRef.current;
    setFields({ ...data });
    try { localStorage.setItem(`form_draft_${id}`, JSON.stringify(data)); setSaved(true); } catch {}
  }, [id]);

  const handleSave = () => {
    saveNow();
    showToast("Draft saved");
  };

  const handleDownload = async () => {
    try {
      showToast("Generating PDF...");
      const blob = await downloadTemplatePdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = (template?.name || "contract").replace(/[^a-zA-Z0-9 ]/g, "").replace(/ +/g, "_") + ".pdf";
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      showToast("PDF downloaded!");
    } catch (err) { showToast(err.message || "Failed", "error"); }
  };

  const openSendModal = async () => {
    try { const d = await getDeals(); setDeals(d); } catch {}
    setSendModal(true);
  };

  const handleSend = async () => {
    if (!sendForm.signerEmail) return showToast("Signer email required", "error");
    setSending(true);
    try {
      const signers = [{ name: sendForm.signerName, email: sendForm.signerEmail, role: "Signer 1" }];
      if (sendForm.signer2Email) signers.push({ name: sendForm.signer2Name, email: sendForm.signer2Email, role: "Signer 2" });
      await sendContractTemplate(id, { signers, deal_id: sendForm.deal_id || undefined, crm_overrides: fields });
      showToast("Sent for signing!");
      setSendModal(false);
    } catch (err) { showToast(err.message || "Failed to send", "error"); }
    setSending(false);
  };

  // ── Render a single line with line number ──
  const Line = ({ children, indent = 0, bold = false, empty = false }) => {
    const num = lineCounter.current++;
    if (empty) return <div style={{ height: 10 }} />;
    return (
      <div style={{ display: "flex", minHeight: 22, alignItems: "baseline" }}>
        <div style={{ width: 28, textAlign: "right", paddingRight: 8, fontSize: 9, color: "#c0c0c0", fontFamily: "monospace", flexShrink: 0, userSelect: "none", lineHeight: "22px" }}>
          {num}
        </div>
        <div style={{ flex: 1, paddingLeft: indent * 16, fontSize: 11, fontFamily: "'Georgia', 'Times New Roman', serif", lineHeight: "22px", color: "#222", fontWeight: bold ? 700 : 400 }}>
          {children}
        </div>
      </div>
    );
  };

  const placeholderMap = useRef({});
  useEffect(() => {
    const map = {};
    (template?.placeholders || []).forEach(p => { map[p.key] = p.label; });
    placeholderMap.current = map;
  }, [template]);

  // Anchor tag labels
  const anchorLabels = {
    "TEXT_1": "Text", "TEXT_2": "Text", "ADDR_1": "Address", "ADDR_2": "Address",
    "PHONE_1": "Phone", "PHONE_2": "Phone", "COMPANY_1": "Company", "COMPANY_2": "Company",
    "NAME_1": "Full Name", "NAME_2": "Full Name",
  };

  // ── Parse content and replace {{placeholders}} and «ANCHORS» with Field components ──
  const renderContent = (text) => {
    if (!text) return null;
    // Strip HTML
    const clean = text
      .replace(/<strong>/g, "**").replace(/<\/strong>/g, "**")
      .replace(/<br\s*\/?>/g, "\n").replace(/<[^>]+>/g, "")
      .replace(/&amp;/g, "&").replace(/&nbsp;/g, " ")
      .replace(/&laquo;/g, "\u00AB").replace(/&raquo;/g, "\u00BB");

    const lines = clean.split("\n");

    return lines.map((line, i) => {
      if (!line.trim()) return <Line key={i} empty />;

      // Skip signature/date/initial anchor lines — rendered in signature section
      if (/[«\u00AB](SIGN_|DATE_|INITIAL_)/i.test(line)) return null;

      // Check for bold markers
      const isBold = line.startsWith("**") && line.endsWith("**");
      const isListItem = line.trim().startsWith("(") || line.trim().startsWith("\u2022");

      // Split on {{placeholder}} and «ANCHOR» tokens
      const parts = [];
      const regex = /\{\{(\w+)\}\}|[\u00AB«](\w+)[\u00BB»]/g;
      let lastIdx = 0;
      let match;
      let partKey = 0;

      const cleanLine = isBold ? line.replace(/\*\*/g, "") : line.replace(/\*\*(.+?)\*\*/g, "$1");

      while ((match = regex.exec(cleanLine)) !== null) {
        if (match.index > lastIdx) {
          parts.push(<span key={`t${partKey++}`}>{cleanLine.substring(lastIdx, match.index)}</span>);
        }
        const fKey = match[1] || match[2]; // match[1] = {{key}}, match[2] = «KEY»
        const label = placeholderMap.current[fKey] || anchorLabels[fKey] || fKey;
        parts.push(<InlineField key={`f_${fKey}_${i}`} fieldKey={fKey} label={label} initialValue={fields[fKey]} onCommit={updateField} onSave={saveNow} />);
        lastIdx = match.index + match[0].length;
      }
      if (lastIdx < cleanLine.length) {
        parts.push(<span key={`t${partKey++}`}>{cleanLine.substring(lastIdx)}</span>);
      }

      return (
        <Line key={i} bold={isBold} indent={isListItem ? 1 : 0}>
          {parts}
        </Line>
      );
    });
  };

  // ── Signature block ──
  // Single signer block — signature line + date on same row, then print name below
  const SignerBlock = ({ label, lineNum }) => (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end" }}>
        <div style={{ display: "flex", alignItems: "baseline", flex: 3 }}>
          <div style={{ width: 28, textAlign: "right", paddingRight: 8, fontSize: 9, color: "#c0c0c0", fontFamily: "monospace", flexShrink: 0 }}>{lineNum}</div>
          <div style={{ flex: 1 }}>
            <div style={{ borderBottom: "1px solid #333", height: 24, marginBottom: 3 }} />
            <div style={{ fontSize: 9, color: "#666", fontFamily: "'Georgia', serif" }}>{label} Signature</div>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ borderBottom: "1px solid #333", height: 24, marginBottom: 3 }} />
          <div style={{ fontSize: 9, color: "#666", fontFamily: "'Georgia', serif" }}>Date</div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", marginTop: 12 }}>
        <div style={{ width: 28, textAlign: "right", paddingRight: 8, fontSize: 9, color: "#c0c0c0", fontFamily: "monospace", flexShrink: 0 }}>{lineNum + 1}</div>
        <div style={{ flex: 1, maxWidth: "60%" }}>
          <div style={{ borderBottom: "1px solid #333", height: 20, marginBottom: 3 }} />
          <div style={{ fontSize: 9, color: "#666", fontFamily: "'Georgia', serif" }}>{label} (print)</div>
        </div>
      </div>
    </div>
  );

  // Determine signer labels from template
  const getSignerLabels = () => {
    const cat = template?.category;
    if (cat === "Lease") return ["Landlord/Seller", "Tenant/Buyer"];
    if (cat === "Listing") return ["Seller", "Agent/Broker"];
    if (cat === "Representation") return ["Buyer", "Agent/Broker"];
    if (cat === "Purchase") return ["Buyer", "Seller"];
    if (cat === "Negotiation") return ["Buyer", "Seller"];
    return ["Party 1", "Party 2"];
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontFamily: font.body }}>Loading...</div>;
  if (!template) return null;

  const content = template.content || {};
  const signerLabels = getSignerLabels();
  // Reset line counter before render
  lineCounter.current = 1;

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      {/* Print styles — only show form, hide toolbar/sidebar */}
      <style>{`
        @media print {
          body > *:not(#root) { display: none !important; }
          #root > * { display: none !important; }
          .print-form-container { display: block !important; position: fixed !important; top: 0; left: 0; right: 0; z-index: 99999; background: #fff !important; box-shadow: none !important; border: none !important; margin: 0 !important; padding: 20px 10px !important; max-width: 100% !important; }
          .no-print { display: none !important; }
          .form-field-input { border-bottom: 1px solid #999 !important; background: transparent !important; -webkit-print-color-adjust: exact; }
        }
      `}</style>

      {/* ── Top Toolbar ── */}
      <div className="no-print" style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 0", marginBottom: 12, flexWrap: "wrap", gap: 8,
        borderBottom: `1px solid ${t.border}`, paddingBottom: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => navigate("/contracts")} style={{
            display: "flex", alignItems: "center", gap: 4, background: "none", border: "none",
            color: t.textSecondary, fontSize: 13, fontFamily: font.body, cursor: "pointer", fontWeight: 600,
          }}>
            &larr; Back to Folder
          </button>
          <span style={{ color: t.border }}>|</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>
            View Form: {template.name}
          </span>
          {saved && <Badge variant="emerald" style={{ fontSize: 8 }}>Saved</Badge>}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <Button variant="outline" size="sm" onClick={handleSave} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/></svg>
            Save
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Preview
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            PDF
          </Button>
          <Button variant="primary" size="sm" onClick={openSendModal} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            Send for Signing
          </Button>
        </div>
      </div>

      {/* ── Form Container ── */}
      <div className="print-form-container" style={{
        background: "#fff", border: "1px solid #d0d0d0", maxWidth: 850,
        boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: "24px 16px 30px 6px",
      }}>
        {/* Form Header */}
        <div style={{ textAlign: "center", marginBottom: 16, paddingLeft: 28 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0a2540", fontFamily: "'Georgia', serif", letterSpacing: 0.5, textTransform: "uppercase" }}>
            {content.title || template.name}
          </div>
          <div style={{ fontSize: 9, color: "#999", fontFamily: "'Georgia', serif", marginTop: 3 }}>
            State of {template.state || "Illinois"}
          </div>
          <div style={{ width: 60, height: 1, background: "#c7a659", margin: "8px auto 0" }} />
        </div>

        {/* Sections */}
        {(content.sections || []).map((section, sIdx) => {
          // Skip the last "Signatures" section — we render it custom
          if (section.id === "signatures") return null;

          return (
            <div key={sIdx} style={{ marginBottom: 8 }}>
              {/* Section title */}
              {section.title && (
                <Line bold>
                  <span style={{ color: "#0a2540", textDecoration: "underline", textUnderlineOffset: 3 }}>
                    {section.title}
                  </span>
                </Line>
              )}
              {/* Section content */}
              {renderContent(section.content)}
              <Line empty />
            </div>
          );
        })}

        {/* ── Signature Section ── */}
        <div style={{ marginTop: 16 }}>
          <div style={{ borderTop: "1px solid #333", paddingTop: 20 }}>
            {signerLabels.map((label, i) => (
              <SignerBlock key={label} label={label} lineNum={lineCounter.current + i * 2} />
            ))}
          </div>

          {/* Footer */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20, paddingTop: 8, borderTop: "1px solid #ddd" }}>
            <div style={{ fontSize: 8, color: "#999", fontFamily: "'Georgia', serif", fontStyle: "italic" }}>
              V1.0 {new Date().toLocaleDateString("en-US", { month: "2-digit", year: "numeric" })}
            </div>
            <div style={{ fontSize: 8, color: "#999", fontFamily: "'Georgia', serif" }}>
              &copy; {new Date().getFullYear()} RealtyFlows LLC — All Rights Reserved
            </div>
          </div>
        </div>
      </div>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: toast.variant === "error" ? "#fee2e2" : "#dcfce7",
          color: toast.variant === "error" ? "#dc2626" : "#16a34a",
          padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600,
          fontFamily: font.body, boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 9999,
        }}>
          {toast.msg}
        </div>
      )}

      {/* ── Send Modal ── */}
      {sendModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: t.surface, borderRadius: 16, padding: 28, maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 16, fontFamily: font.display }}>Send for Signing</div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: t.textSecondary, fontFamily: font.body, display: "block", marginBottom: 4 }}>Link to Deal (optional)</label>
              <SearchSelect
                options={deals.map(d => ({ value: d.id, label: `${d.client_name || "Deal"} — ${d.property_address || d.status}` }))}
                value={sendForm.deal_id}
                onChange={v => setSendForm({ ...sendForm, deal_id: v })}
                placeholder="Search deals..."
              />
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 6, fontFamily: "monospace" }}>SIGNER 1</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              <input value={sendForm.signerName} onChange={e => setSendForm({ ...sendForm, signerName: e.target.value })} placeholder="Full Name" style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none" }} />
              <input value={sendForm.signerEmail} onChange={e => setSendForm({ ...sendForm, signerEmail: e.target.value })} placeholder="Email *" style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none" }} />
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 6, fontFamily: "monospace" }}>SIGNER 2 (optional)</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
              <input value={sendForm.signer2Name} onChange={e => setSendForm({ ...sendForm, signer2Name: e.target.value })} placeholder="Full Name" style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none" }} />
              <input value={sendForm.signer2Email} onChange={e => setSendForm({ ...sendForm, signer2Email: e.target.value })} placeholder="Email" style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none" }} />
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <Button variant="outline" onClick={() => setSendModal(false)} style={{ flex: 1 }}>Cancel</Button>
              <Button variant="primary" onClick={handleSend} disabled={sending} style={{ flex: 1 }}>{sending ? "Sending..." : "Send for Signing"}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
